/**
 * Represents a type that is a deep partial version of another type -- generally a nested object.
 *
 * This utility type recursively makes all properties of a given type `T` optional.
 * If a property is an object, it applies the same transformation to its properties.
 *
 * @template T - The type to be transformed into a deep partial type.
 *
 * @example
 * ```typescript
 * interface Example {
 *   a: number;
 *   b: {
 *     c: string;
 *     d: boolean;
 *   };
 * }
 *
 * DeepPartial<Example> will be equivalent to:
 * {
 *   a?: number;
 *   b?: {
 *     c?: string;
 *     d?: boolean;
 *   };
 * }
 * ```
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Merges two objects deeply, combining properties from the source object into the target object.
 * If a property is an object in both the target and source, it will recursively merge those objects.
 * If a property is not an object, the source property will overwrite the target property.
 * If either the target or source is null or undefined, the non-null/undefined value will be returned.
 * Array values will be overwritten, not merged.
 *
 * @template T - The type of the target object.
 * @param {T} target - The target object to merge properties into.
 * @param {DeepPartial<T>} source - The source object containing properties to merge into the target.
 * @returns {T} - The merged object.
 */
export function deepMerge<T extends object>(target: T, source: DeepPartial<T>): T {
  if (isNullOrUndefined(target) || isNullOrUndefined(source)) {
    return (source ?? target) as T;
  }
  return Object.keys(source).reduce(
    (acc, k) => {
      const key = k as keyof T;
      const sourceValue = source[key];
      const targetValue = target[key];
      let value: T[keyof T];
      if (isObject(sourceValue)) {
        value = deepMerge(targetValue as object, sourceValue as object) as T[keyof T];
      } else {
        value = (sourceValue ?? targetValue) as T[keyof T];
      }
      return { ...acc, [key]: value };
    },
    { ...target },
  );
}

function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function isObject(item: unknown): boolean {
  return typeof item === 'object' && !Array.isArray(item) && !!item;
}
